import { v4 as uuid } from 'uuid'
import { computed, ref, watch } from 'vue'

import { BillGenerator } from '@last/core'
import { useDialog } from '@last/core-ui/paprika'

import { useTabs } from '@/composables/useTabs'
import { t } from '@/i18n'
import CashMachine from '@/integrations/cashmachine/cashmachine'
import Dataphone from '@/integrations/dataphone/dataphone'
import { iZettlePlugin } from '@/integrations/iZettlePlugin'
import Printer from '@/integrations/printer/printer'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { SumUpPlugin } from '@/integrations/SumUpPlugin'
import { logger } from '@/monitoring'
import { useToast } from '@/plugins/toast/toast'
import { useConfigStore } from '@/store/config'
import { usePromotionsStore } from '@/store/promotions'
import { useTabsStore } from '@/store/tabs'
import type { BillWithPayments, PaymentMethod, TabProduct } from '@/types'
import { CustomError } from '@/utils/custom-error'

import { useKitchenOrders } from './useKitchenOrders'
import { useMoney } from './useMoney'

export const useCheckout = (tabId: string) => {
  const { currency } = useMoney()
  const dialog = useDialog()
  const toast = useToast()

  const { tab, allProducts, allBills, globalDiscount, unsentProducts, hasNonBilledProducts } =
    useTabs(tabId)

  const { config } = useConfigStore()
  const { deleteTabPromotion, getTabGlobalPromotion } = usePromotionsStore()
  const {
    addBill,
    addPayment,
    updateBillPayers,
    setPreferredPaymentMethod,
    removeTabBills,
    generatePendingBill
  } = useTabsStore()

  const selectedBillId = ref<string>()
  const selectedProductsIds = ref<{ id: string; uniqueId: string }[]>([])

  const isCharging = ref<boolean>(false)
  const receivedAmount = ref<number>()
  const customAmount = ref<number>()

  const payers = ref<number>()

  const isDelivery = computed(() => {
    return (
      tab.value &&
      tab.value.pickupType &&
      ['delivery', 'ownDelivery'].includes(tab.value.pickupType)
    )
  })

  const pendingBill = computed(() => {
    if (checkoutComplete.value) return null

    let selectedProducts = []
    if (selectedProductsIds.value.length > 0) {
      const tabProducts = allProducts.value
      selectedProducts = Object.values(
        selectedProductsIds.value
          .map(selection => {
            const product = tabProducts.find(tabProduct => tabProduct.id === selection.id)!
            if (product.discountType === '2x1') {
              product.discountType = undefined
              product.discountAmount = undefined
            }
            return product
          })
          .reduce(
            (products, product) => {
              products[product.id] = products[product.id] || {
                ...product,
                quantity: 0
              }
              products[product.id].quantity += 1
              return products
            },
            {} as Record<string, TabProduct>
          )
      )
    } else {
      selectedProducts = allProducts.value.map(product => {
        return {
          ...product,
          quantity: product.notBilledQuantity ?? 0
        }
      })
    }

    return BillGenerator.generateProductsBill({
      products: selectedProducts,
      company: config.company,
      tab: tab.value,
      discount: globalDiscount.value,
      taxRate: config.taxRate,
      ticketInfo: config.ticketInfo
    }) as BillWithPayments
  })

  const bills = computed(() => {
    const existingBills = allBills.value
    if (checkoutComplete.value) {
      return existingBills
    } else {
      const currentBillWithPaymentInfo = BillGenerator.addPaymentInfo(
        pendingBill.value!,
        []
      ) as BillWithPayments

      return [currentBillWithPaymentInfo, ...existingBills]
    }
  })

  const selectedBill = computed(() => {
    return bills.value.find(bill => bill.id === selectedBillId.value) || bills.value[0]
  })

  const pendingBills = computed(() => {
    return bills.value.filter(bill => bill.pending && bill.pending > 0) || []
  })

  const paymentMethods = computed<PaymentMethod[]>(() => {
    const methods = [...config.paymentMethodsConfig]
      .filter(method => (isDelivery.value ? method.availableForDelivery : true))
      .sort((a, b) => a.position - b.position)
      .map(method => {
        const type = method.name.toLowerCase()
        return {
          ...method,
          name: t(`bill.${type}`, method.name),
          type
        }
      })

    if (
      isDelivery.value &&
      tab.value?.source &&
      tab.value.source !== 'OwnDelivery' &&
      tab.value.source !== 'Shop'
    ) {
      const sourceAsMethod = {
        name: tab.value.source,
        type: tab.value.source.toLowerCase()
      } as PaymentMethod
      return [sourceAsMethod, ...methods]
    }

    return methods
  })

  const paymentMethod = ref<PaymentMethod>(paymentMethods.value[0])

  const negativePayments = computed(() => {
    if (!tab.value) return []
    const negativePayments = []
    for (const bill of bills.value) {
      for (const payment of bill.payments) {
        if (payment.amount < 0) {
          negativePayments.push(payment)
        }
      }
    }

    return negativePayments
  })

  const pending = computed(() => {
    return selectedBill.value?.pending ?? 0
  })

  const toPay = computed(() => {
    if (customAmount.value !== undefined) {
      return customAmount.value
    }
    return pending.value
  })

  const toCharge = computed(() => {
    return receivedAmount.value ? receivedAmount.value : toPay.value
  })

  const toChange = computed(() => {
    const diff = toCharge.value - toPay.value
    return !hasTip.value && diff > 0 ? diff : 0
  })

  const toTip = computed(() => {
    const diff = toCharge.value - toPay.value
    return hasTip.value && diff > 0 ? diff : 0
  })

  const hasTip = computed(() => {
    return paymentMethod.value.type !== 'cash'
  })

  async function charge(avoidConfirm?: boolean): Promise<void> {
    isCharging.value = true

    if (paymentMethod.value.type === 'cash') {
      const paymentId = uuid()
      const change = toChange.value
      executePayment(paymentId)
      if (change > 0 && !avoidConfirm) {
        showChangeConfirmationDialog(change)
      }
    } else {
      if (toTip.value > 0 && !avoidConfirm) {
        showConfirmTipDialog()
      } else {
        await chargeWithMethod()
      }
    }

    if (paymentMethod.value.openDrawer) {
      Printer.openCashDrawer()
    }

    isCharging.value = false
  }

  async function chargeWithMethod(): Promise<void> {
    try {
      const paymentId = uuid()
      let metadata = null

      const isTerminal = config.terminals.some(
        terminal => terminal.paymentMethod === paymentMethod.value.name
      )
      if (paymentMethod.value.type === 'izettle') {
        await iZettlePlugin.charge({ amount: toCharge.value })
      } else if (paymentMethod.value.type === 'sumup') {
        await SumUpPlugin.charge({ amount: toCharge.value })
      } else if (CashMachine.methods.includes(paymentMethod.value.type)) {
        const charged = await CashMachine.charge(toCharge.value)
        if (charged === 0)
          throw new CustomError('PAYMENT_CANCELLED', t('checkout.payment-cancelled'))
      } else if (paymentMethod.value.type === 'dataphone') {
        metadata = await Dataphone.charge({
          tabId: tabId,
          billId: selectedBill.value.id,
          paymentId,
          amount: toCharge.value,
          tip: toTip.value
        })
        if (metadata.tip) updateTip(metadata.tip)
      } else if (isTerminal) {
        const chargeResponse = await Dataphone.charge({
          tabId: tabId,
          billId: selectedBill.value.id,
          paymentId,
          amount: toCharge.value,
          tip: toTip.value
        })
        if (chargeResponse.tip) updateTip(chargeResponse.tip)
      }
      executePayment(paymentId, metadata)
    } catch (error) {
      toast({
        type: 'error',
        title: (error as Error).message ?? t('notifications.payment-failed')
      })
      if (!(error instanceof CustomError)) {
        logger.warn('Unhandled payment error:', error as Error)
      }
    }
  }

  function executePayment(paymentId: string, metadata?: any): void {
    const bill = selectedBill.value
    const billId = bill.id
    const change = toChange.value
    const tip = toTip.value
    const amount = toCharge.value - change

    addBill({ tabId, bill })
    addPayment({
      tabId,
      billId,
      type: paymentMethod.value.type,
      amount,
      change,
      tip,
      paymentId,
      metadata,
      tillId: undefined
    })

    customAmount.value = undefined

    const printBill = bills.value.find(bill => bill.id === billId)!
    const remaining = printBill.pending ?? selectedBill.value.pending

    updateBillPayers({ billId, payers: payers.value ?? null })

    if (remaining === 0) {
      paymentMethod.value = paymentMethods.value[0]
    }

    toast({
      type: 'success',
      title: t('checkout.notifications.payment-successful'),
      message: remaining > 0 ? t('checkout.notifications.bill-pending') : undefined
    })

    if (
      remaining <= 0 &&
      (((tab.value.tables?.length || 0) === 0 && config.organizationConfig.takeawayAutoprint) ||
        config.organizationConfig.restaurantAutoprint)
    ) {
      TicketPrinter.printBill(printBill)
    }

    onPaymentSuccess()
  }

  function onPaymentSuccess(): void {
    selectedProductsIds.value = []
    receivedAmount.value = undefined
    if (selectedBill.value.pending === 0 && pendingBill.value) {
      selectedBillId.value = pendingBill.value.id
    } else if (selectedBill.value.pending === 0 && pendingBills.value.length > 0) {
      selectedBillId.value = pendingBills.value[0].id
    }
    isCharging.value = false
  }

  const checkoutComplete = computed(() => {
    const tabProducts = allProducts.value
    return (
      tabProducts
        .map(product => product.notBilledQuantity)
        .reduce((total, quantity) => total + quantity, 0) === 0
    )
  })

  const checkoutCompleteWithPayments = computed<boolean>(() => {
    const tabProducts = allProducts.value

    const allProductsBilled =
      tabProducts
        .map(product => product.notBilledQuantity)
        .reduce((total, quantity) => total + quantity, 0) === 0
    const pendingPayments = bills.value
      .map(bill => bill.pending)
      .reduce((sum, pending) => sum + pending, 0)

    return allProductsBilled && pendingPayments === 0
  })

  function showConfirmTipDialog(): void {
    dialog({
      title: t('checkout.confirm-tip', {
        tip: currency(toTip.value)
      }),
      secondaryLabel: t('ctas.cancel'),
      onConfirm: () => {
        charge(true)
      },
      onSecondary: () => {
        isCharging.value = false
      },
      onCancel: () => {
        isCharging.value = false
      }
    })
  }

  function showChangeConfirmationDialog(change: number): void {
    dialog({
      title: t('checkout.change'),
      content: `${t('checkout.change-info')} ${currency(change)}`
    })
  }

  async function removeDiscount(): Promise<void> {
    await deleteTabPromotion({
      tabId,
      tabPromotion: getTabGlobalPromotion(tabId)!
    })
  }

  function updateTip(tip: number): void {
    receivedAmount.value = toCharge.value + tip
  }

  function setCustomAmount(amount: number | null): void {
    if (amount === null) {
      customAmount.value = undefined
      receivedAmount.value = undefined
      return
    }
    customAmount.value = amount
    receivedAmount.value = undefined
  }

  function generateDeliveryBills() {
    if (
      (allBills.value.length === 0 ||
        hasNonBilledProducts.value ||
        unsentProducts.value.length > 0) &&
      tab.value.activationTime
    ) {
      removeTabBills(tabId)
      generatePendingBill({
        tabId: tabId,
        bill: pendingBill.value!,
        payments: negativePayments.value
      })
      const bills = allBills.value
      bills.forEach(bill => TicketPrinter.printBill(bill))
      if (config.enableKitchenOrders) {
        const { sendKitchenOrder } = useKitchenOrders(tabId)
        sendKitchenOrder('all')
      }
    }
  }

  watch(paymentMethod, () => {
    receivedAmount.value = undefined
    if (isDelivery.value) {
      setPreferredPaymentMethod({
        tabId: tabId,
        preferredPaymentMethod: paymentMethod.value.type
      })
    }
  })

  watch(selectedBill, () => {
    receivedAmount.value = undefined
  })

  return {
    bills,
    selectedBill,
    selectedBillId,
    pendingBill,
    pendingBills,
    selectedProductsIds,
    checkoutComplete,
    checkoutCompleteWithPayments,
    paymentMethods,
    paymentMethod,
    isCharging,
    pending,
    toCharge,
    toChange,
    toTip,
    toPay,
    hasTip,
    receivedAmount,
    customAmount,
    payers,
    isDelivery,
    generateDeliveryBills,
    charge,
    removeDiscount,
    setCustomAmount
  }
}
